import React, { useState, useEffect, useRef } from "react";
import { X, Info, Trash2 } from "lucide-react";
import { calculateCPFRatioFromStrings } from "./CPFCalculator";
import { useNavigate } from "react-router-dom";
import ReactDOM from "react-dom";
import {
  getMealPlanTypeList,
  updateMealPlanDetails,
  getDuplicateMealPlanDetails,
} from "../../../services/mealPlans";
import { updateGlobalTemplateMealTime } from "../../../services/globalTemplates";
import { updateAdhocMealPlanMealTime } from "../../../services/adhocMealPlans";
import { useAuth } from "../../../context/auth/AuthContext";
import useCustomScrollbar from "../../styles/useCustomScrollbar";
import { ReactComponent as DeleteIcon } from "../../../assects/svg/client_details/delete.svg";
import { Button } from "../../Ui_Components/Button";

interface MealPlanDuplicateAndTimingsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  userId: number;
  isEditMode?: boolean;
  existingMealTimings?: MealTiming[];
  mealPlanId?: string;
  isGlobalTemplateMode?: boolean;
  isAdhocMealMode?: boolean;
  onShowToast?: (
    type: "success" | "error" | "warning" | "info",
    title: string,
    message?: string,
    duration?: number,
  ) => void;
  duplicateData?: Step1Data | null;
}

interface MealTiming {
  id: string;
  mealType: string;
  time: { hour: string; minute: string; period: string };
  fk_user_meal?: number;
}

interface Step1Data {
  planType: string;
  totalCalories: { min: string; max: string };
  totalAdjProtein: { min: string; max: string };
  totalNetCarbs: { min: string; max: string };
  totalFats: { min: string; max: string };
  totalGL: { min: string; max: string };
  cpfRatio: {
    min: { c: string; p: string; f: string };
    max: { c: string; p: string; f: string };
  };
  instructions: string;
  sourceMealPlanId?: string;
}

interface CustomDropdownProps {
  value: string;
  placeholder: string;
  options: string[];
  onChange: (val: string) => void;
  disabled?: boolean;
  error?: boolean;
}

interface MealCardProps {
  meal: MealTiming;
  isSelected: boolean;
  showMenu: boolean;
  onSelect: () => void;
  onDelete: () => void;
  formatTime: (hour: string, minute: string, period: string) => string;
}

const MealCard: React.FC<MealCardProps> = ({
  meal,
  isSelected,
  showMenu,
  onSelect,
  onDelete,
  formatTime,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom - 10;
      const dropdownHeight = 96;

      let top: number;
      if (spaceBelow >= dropdownHeight) {
        top = buttonRect.bottom + 4;
      } else {
        top = buttonRect.top - dropdownHeight - 4;
      }

      setDropdownPosition({
        top,
        left: buttonRect.right - 128,
        width: 128,
        maxHeight: dropdownHeight,
      });
    }
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div
      onClick={onSelect}
      className={`p-3 border-[0.5px] rounded-md cursor-pointer transition-all bg-white ${
        isSelected
          ? "border-[#0DB5B5] bg-white"
          : "border-[#E0E0E0] hover:border-[#0DB5B5]"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-medium text-[#1F1F1F] text-sm 3xl:text-base mb-1">
            {meal.mealType}
          </h4>
          <p className="text-[#8E8E93] text-sm 3xl:text-base">
            {formatTime(meal.time.hour, meal.time.minute, meal.time.period)}
          </p>
        </div>
        {showMenu && (
          <div className="relative">
            <button
              ref={buttonRef}
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(!showDropdown);
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <svg
                className="w-4 h-4 text-[#646464]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>

            {showDropdown &&
              dropdownPosition &&
              ReactDOM.createPortal(
                <div
                  ref={dropdownRef}
                  style={{
                    position: "fixed",
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`,
                    zIndex: 9999,
                  }}
                  className="bg-white border border-[#E0E0E0] rounded-md shadow-lg p-2"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDropdown(false);
                      onSelect();
                    }}
                    className="w-full px-4 py-2 text-left text-sm 3xl:text-base text-[#1F1F1F] hover:bg-[#F5F5F5] transition-colors flex items-center gap-2 rounded-md"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDropdown(false);
                      onDelete();
                    }}
                    className="w-full px-4 py-2 text-left text-sm 3xl:text-base text-[#FF3B30] hover:bg-[#FFF5F5] transition-colors flex items-center gap-2 rounded-md"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                </div>,
                document.body,
              )}
          </div>
        )}
      </div>
    </div>
  );
};

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  placeholder,
  options,
  onChange,
  disabled,
  error,
}) => {
  const [open, setOpen] = React.useState(false);
  const [dropdownPosition, setDropdownPosition] = React.useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: "bottom" | "top";
  } | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (open && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom - 10;
      const spaceAbove = buttonRect.top - 10;
      const maxDropdownHeight = 240;

      let top: number;
      let placement: "bottom" | "top";
      let maxHeight: number;

      if (spaceBelow >= maxDropdownHeight || spaceBelow >= spaceAbove) {
        placement = "bottom";
        top = buttonRect.bottom + 4;
        maxHeight = Math.min(maxDropdownHeight, spaceBelow);
      } else {
        placement = "top";
        const availableHeight = Math.min(maxDropdownHeight, spaceAbove);
        top = buttonRect.top - availableHeight - 4;
        maxHeight = availableHeight;
      }

      setDropdownPosition({
        top,
        left: buttonRect.left,
        width: buttonRect.width,
        maxHeight,
        placement,
      });
    }
  }, [open]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  React.useEffect(() => {
    const handleScroll = (event: Event) => {
      if (
        dropdownRef.current &&
        dropdownRef.current.contains(event.target as Node)
      ) {
        return;
      }

      if (open) {
        setOpen(false);
      }
    };

    if (open) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  React.useEffect(() => {
    const handleResize = () => {
      if (open) {
        setOpen(false);
      }
    };

    if (open) {
      window.addEventListener("resize", handleResize);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [open]);

  return (
    <div className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className={`w-full px-4 py-2 border rounded-md text-sm 3xl:text-base font-inter bg-white
        flex items-center justify-between focus:outline-none
        ${error ? "border-red-500" : "border-[#E0E0E0] focus:border-[#0DB5B5]"}
        ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      >
        <span className={value ? "text-[#202020]" : "text-[#9E9E9E]"}>
          {value || placeholder}
        </span>

        <svg
          className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open &&
        dropdownPosition &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: `${dropdownPosition.maxHeight}px`,
              zIndex: 9999,
            }}
            className="bg-white border border-[#E0E0E0] rounded-md shadow-lg overflow-y-auto custom-scrollbar-overlay"
            onScroll={(e) => e.stopPropagation()}
          >
            <div className="py-2">
              {options.map((opt) => (
                <div
                  key={opt}
                  onClick={() => {
                    onChange(opt);
                    setOpen(false);
                  }}
                  className={`px-4 py-2 mx-2 my-1 text-sm 3xl:text-base font-inter cursor-pointer rounded-md
                  ${
                    value === opt
                      ? "bg-[#E6FBFB] text-[#0DB5B5]"
                      : "text-[#202020] hover:bg-[#F5F5F5]"
                  }`}
                >
                  {opt}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const isCaloriesValueValid = (value: string): boolean => {
  if (value === "" || value === "-") return true;
  const integerPart = value.split(".")[0];
  const digitsOnly = integerPart.replace(/[^0-9]/g, "");
  return digitsOnly.length <= 4;
};

const MealPlanDuplicateAndTimingsPopup: React.FC<MealPlanDuplicateAndTimingsPopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userId,
  isEditMode = false,
  existingMealTimings = [],
  mealPlanId = "",
  isGlobalTemplateMode = false,
  isAdhocMealMode = false,
  onShowToast,
  duplicateData = null,
}) => {
  useCustomScrollbar();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showInstructions, setShowInstructions] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );
  const [isCreating, setIsCreating] = useState(false);
  // ── NEW: delete confirmation state for instructions ──────────────────────
  const [showDeleteInstructionsConfirm, setShowDeleteInstructionsConfirm] =
    useState(false);

  const [hasChanges, setHasChanges] = useState(false);
  const [initialDuplicateData, setInitialDuplicateData] =
    useState<Step1Data | null>(null);

  const [showPlanTypeDropdown, setShowPlanTypeDropdown] = useState(false);
  const [planTypeSearchTerm, setPlanTypeSearchTerm] = useState("");
  const planTypeDropdownRef = useRef<HTMLDivElement>(null);

  const [planTypes, setPlanTypes] = useState<string[]>([]);
  const [isPlanTypesLoading, setIsPlanTypesLoading] = useState(false);

  // ── NEW: Track fk_user_meal IDs of meals deleted during this edit session ──
  const [deletedMealIds, setDeletedMealIds] = useState<number[]>([]);
  // Ref mirrors the state so handleUpdate always reads the latest value
  // without needing deletedMealIds in its dependency/closure
  const deletedMealIdsRef = useRef<number[]>([]);
  const isSubmittingRef = useRef(false);

  const [step1Data, setStep1Data] = useState<Step1Data>({
    planType: "",
    totalCalories: { min: "", max: "" },
    totalAdjProtein: { min: "", max: "" },
    totalNetCarbs: { min: "", max: "" },
    totalFats: { min: "", max: "" },
    totalGL: { min: "", max: "" },
    cpfRatio: {
      min: { c: "0", p: "0", f: "0" },
      max: { c: "0", p: "0", f: "0" },
    },
    instructions: "",
  });

  const [mealTimings, setMealTimings] = useState<MealTiming[]>([]);
  const [selectedMealId, setSelectedMealId] = useState<string | null>(null);
  const [currentMeal, setCurrentMeal] = useState({
    mealType: "",
    hour: "Hr",
    minute: "Min",
    period: "AM",
  });

  const allMealTypes = [
    "On Waking Up",
    "Pre Breakfast",
    "Breakfast",
    "Mid Morning",
    "Brunch",
    "Lunch",
    "Post Lunch",
    "Tea Time",
    "Dinner",
    "Post Dinner",
    "Snacks",
    "Bed Time",
    "Other",
  ];

  const availableMealTypes = allMealTypes.filter(
    (mealType) =>
      !mealTimings.some(
        (meal) => meal.mealType === mealType && meal.id !== selectedMealId,
      ),
  );

  const hours = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, "0"),
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    String(i).padStart(2, "0"),
  );

  const filteredPlanTypes = planTypes.filter((type) =>
    type.toLowerCase().includes(planTypeSearchTerm.toLowerCase()),
  );

  useEffect(() => {
    if (isOpen && duplicateData) {
      setStep1Data(duplicateData);
      setInitialDuplicateData(JSON.parse(JSON.stringify(duplicateData)));
      setShowInstructions(!!duplicateData.instructions);
      setHasChanges(false);
      setPlanTypeSearchTerm(duplicateData.planType);
    }
  }, [isOpen, duplicateData]);

  useEffect(() => {
    if (isOpen && isEditMode && existingMealTimings.length > 0) {
      setMealTimings([...existingMealTimings]);
    }
  }, [isOpen, isEditMode, existingMealTimings]);

  // ── NEW: Reset deletedMealIds when popup opens ────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setDeletedMealIds([]);
      deletedMealIdsRef.current = [];
    }
  }, [isOpen]);

  useEffect(() => {
    setPlanTypeSearchTerm(step1Data.planType);
  }, [step1Data.planType]);

  useEffect(() => {
    if (initialDuplicateData && !isEditMode) {
      const currentDataStr = JSON.stringify(step1Data);
      const initialDataStr = JSON.stringify(initialDuplicateData);
      setHasChanges(currentDataStr !== initialDataStr);
    }
  }, [step1Data, initialDuplicateData, isEditMode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        planTypeDropdownRef.current &&
        !planTypeDropdownRef.current.contains(event.target as Node)
      ) {
        setShowPlanTypeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const fetchPlanTypes = async () => {
      setIsPlanTypesLoading(true);
      try {
        const data = await getMealPlanTypeList();
        if (data.success === "1" && Array.isArray(data.meal_plan_data)) {
          setPlanTypes(
            data.meal_plan_data.map(
              (item: { meal_plan_type_name: string }) =>
                item.meal_plan_type_name.trim(),
            ),
          );
        } else {
          console.error("Unexpected response shape for plan types:", data);
        }
      } catch (err) {
        console.error("Failed to fetch meal plan types:", err);
        onShowToast?.(
          "error",
          "Failed to load plan types",
          "Could not retrieve plan type options. Please try again.",
        );
      } finally {
        setIsPlanTypesLoading(false);
      }
    };

    fetchPlanTypes();
  }, [isOpen]);

  useEffect(() => {
    const minResult = calculateCPFRatioFromStrings(
      step1Data.totalNetCarbs.min,
      step1Data.totalAdjProtein.min,
      step1Data.totalFats.min,
      step1Data.totalCalories.min,
    );

    const maxResult = calculateCPFRatioFromStrings(
      step1Data.totalNetCarbs.max,
      step1Data.totalAdjProtein.max,
      step1Data.totalFats.max,
      step1Data.totalCalories.max,
    );

    setStep1Data((prev) => ({
      ...prev,
      cpfRatio: {
        min: minResult.ratio,
        max: maxResult.ratio,
      },
    }));
  }, [
    step1Data.totalNetCarbs.min,
    step1Data.totalNetCarbs.max,
    step1Data.totalAdjProtein.min,
    step1Data.totalAdjProtein.max,
    step1Data.totalFats.min,
    step1Data.totalFats.max,
    step1Data.totalCalories.min,
    step1Data.totalCalories.max,
  ]);

  const validateMinMaxField = (field: keyof Step1Data, label: string) => {
    const value = step1Data[field] as { min: string; max: string };

    if (value.min && value.max) {
      const min = parseFloat(value.min);
      const max = parseFloat(value.max);

      if (!isNaN(min) && !isNaN(max) && min > max) {
        setValidationErrors((prev) => ({
          ...prev,
          [field]: `${label} min value cannot be greater than max value`,
        }));
      } else {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  useEffect(() => {
    validateMinMaxField("totalCalories", "Total calories");
  }, [step1Data.totalCalories.min, step1Data.totalCalories.max]);

  useEffect(() => {
    validateMinMaxField("totalAdjProtein", "Total Adj.Protein");
  }, [step1Data.totalAdjProtein.min, step1Data.totalAdjProtein.max]);

  useEffect(() => {
    validateMinMaxField("totalNetCarbs", "Total Net Carbs");
  }, [step1Data.totalNetCarbs.min, step1Data.totalNetCarbs.max]);

  useEffect(() => {
    validateMinMaxField("totalFats", "Total fats");
  }, [step1Data.totalFats.min, step1Data.totalFats.max]);

  useEffect(() => {
    validateMinMaxField("totalGL", "Total GL");
  }, [step1Data.totalGL.min, step1Data.totalGL.max]);

  const isStep1Complete = () => {
    const hasNegativeCPF =
      parseFloat(step1Data.cpfRatio.min.c) < 0 ||
      parseFloat(step1Data.cpfRatio.min.p) < 0 ||
      parseFloat(step1Data.cpfRatio.min.f) < 0 ||
      parseFloat(step1Data.cpfRatio.max.c) < 0 ||
      parseFloat(step1Data.cpfRatio.max.p) < 0 ||
      parseFloat(step1Data.cpfRatio.max.f) < 0;

    const isDuplicateMode = !!duplicateData && !isEditMode;
    const hasRequiredChanges = isDuplicateMode ? hasChanges : true;

    return (
      step1Data.planType &&
      step1Data.totalCalories.min &&
      step1Data.totalCalories.max &&
      step1Data.totalAdjProtein.min &&
      step1Data.totalAdjProtein.max &&
      step1Data.totalNetCarbs.min &&
      step1Data.totalNetCarbs.max &&
      step1Data.totalFats.min &&
      step1Data.totalFats.max &&
      step1Data.totalGL.min &&
      step1Data.totalGL.max &&
      Object.keys(validationErrors).length === 0 &&
      !hasNegativeCPF &&
      hasRequiredChanges
    );
  };

  const handleAddOrUpdateMeal = () => {
    if (
      !currentMeal.mealType ||
      currentMeal.hour === "Hr" ||
      currentMeal.minute === "Min"
    ) {
      return;
    }

    if (selectedMealId) {
      setMealTimings((prev) =>
        prev.map((meal) =>
          meal.id === selectedMealId
            ? {
                ...meal,
                mealType: currentMeal.mealType,
                time: {
                  hour: currentMeal.hour,
                  minute: currentMeal.minute,
                  period: currentMeal.period,
                },
              }
            : meal,
        ),
      );
      setSelectedMealId(null);
    } else {
      const newMeal: MealTiming = {
        id: Date.now().toString(),
        mealType: currentMeal.mealType,
        time: {
          hour: currentMeal.hour,
          minute: currentMeal.minute,
          period: currentMeal.period,
        },
      };
      setMealTimings((prev) => [...prev, newMeal]);
    }

    setCurrentMeal({
      mealType: "",
      hour: "Hr",
      minute: "Min",
      period: "AM",
    });
  };

  const handleSelectMeal = (meal: MealTiming) => {
    setSelectedMealId(meal.id);
    setCurrentMeal({
      mealType: meal.mealType,
      hour: meal.time.hour,
      minute: meal.time.minute,
      period: meal.time.period,
    });
  };

  // ── UPDATED: Also record fk_user_meal in deletedMealIds when deleting ────
  const handleDeleteMeal = (id: string) => {
    const mealToDelete = mealTimings.find((meal) => meal.id === id);

    // If this meal came from the server (has a real fk_user_meal), track its ID
    // so we can send "delete": "true" to the API in handleUpdate.
    if (mealToDelete?.fk_user_meal) {
      const updated = [...deletedMealIdsRef.current, mealToDelete.fk_user_meal!];
      deletedMealIdsRef.current = updated;
      setDeletedMealIds(updated);
    }

    setMealTimings((prev) => prev.filter((meal) => meal.id !== id));

    if (selectedMealId === id) {
      setSelectedMealId(null);
      setCurrentMeal({
        mealType: "",
        hour: "Hr",
        minute: "Min",
        period: "AM",
      });
    }
  };

  const handleReset = () => {
    setCurrentMeal({
      mealType: "",
      hour: "Hr",
      minute: "Min",
      period: "AM",
    });
    setSelectedMealId(null);
  };

  // ── UPDATED: handleUpdate sends deletes first, then updates — matches Postman ──
const handleUpdate = async () => {
  if (isSubmittingRef.current) return;
  isSubmittingRef.current = true;

  if (mealTimings.length === 0 && deletedMealIdsRef.current.length === 0) {
    isSubmittingRef.current = false;
    onShowToast?.("warning", "No meal timings", "Please add at least one meal timing");
    return;
  }

  setIsCreating(true);

  const callMealTimeUpdate = (body: any) =>
    isGlobalTemplateMode
      ? updateGlobalTemplateMealTime(body)
      : isAdhocMealMode
        ? updateAdhocMealPlanMealTime(body)
        : updateMealPlanDetails(body);

  try {
    const currentDeletedIds = deletedMealIdsRef.current;

    // ── STEP 1: Deletes first ──────────────────────────────────────────────
    if (currentDeletedIds.length > 0) {
      const deleteBody = {
        meal_updates: currentDeletedIds.map((mealId) => isGlobalTemplateMode
          ? { meal_id: mealId, fk_template: parseInt(mealPlanId), delete: "true" }
          : isAdhocMealMode
            ? { meal_id: mealId, delete: true }
            : { meal_id: mealId, delete: "true" }),
      };

      console.log("🗑️ DELETE request:", JSON.stringify(deleteBody, null, 2));

      const deleteResult = await callMealTimeUpdate(deleteBody);
      console.log("🗑️ DELETE response:", deleteResult);

      if (deleteResult.success !== "1") {
        throw new Error(deleteResult.message || "Failed to delete meal timings");
      }
    }

    // ── STEP 2: Only changed + new meals ──────────────────────────────────
    const changedExistingMeals = mealTimings.filter((meal) => {
      if (!meal.fk_user_meal || meal.fk_user_meal === 0) return false;
      const original = existingMealTimings.find(
        (orig) => orig.fk_user_meal === meal.fk_user_meal,
      );
      if (!original) return false;
      return (
        original.time.hour !== meal.time.hour ||
        original.time.minute !== meal.time.minute ||
        original.time.period !== meal.time.period ||
        original.mealType !== meal.mealType
      );
    });

    const newMeals = mealTimings.filter(
      (meal) => !meal.fk_user_meal || meal.fk_user_meal === 0,
    );

    const updateCreateEntries = [
      ...changedExistingMeals.map((meal) => isGlobalTemplateMode
        ? { meal_id: meal.fk_user_meal!, fk_template: parseInt(mealPlanId), hour: parseInt(meal.time.hour, 10), minute: parseInt(meal.time.minute, 10), period: meal.time.period, meal_type_name: meal.mealType }
        : isAdhocMealMode
          ? { meal_id: meal.fk_user_meal!, fk_adhoc_plan: parseInt(mealPlanId), hour: parseInt(meal.time.hour, 10), minute: parseInt(meal.time.minute, 10), period: meal.time.period, meal_type_name: meal.mealType }
          : { meal_id: meal.fk_user_meal!, hour: parseInt(meal.time.hour, 10), minute: parseInt(meal.time.minute, 10), period: meal.time.period, meal_type_name: meal.mealType, fk_meal_plan: parseInt(mealPlanId) }),
      ...newMeals.map((meal) => isGlobalTemplateMode
        ? { meal_id: 0, fk_template: parseInt(mealPlanId), hour: parseInt(meal.time.hour, 10), minute: parseInt(meal.time.minute, 10), period: meal.time.period, meal_type_name: meal.mealType }
        : isAdhocMealMode
          ? { meal_id: 0, fk_adhoc_plan: parseInt(mealPlanId), hour: parseInt(meal.time.hour, 10), minute: parseInt(meal.time.minute, 10), period: meal.time.period, meal_type_name: meal.mealType }
          : { meal_id: 0, hour: parseInt(meal.time.hour, 10), minute: parseInt(meal.time.minute, 10), period: meal.time.period, meal_type_name: meal.mealType, fk_meal_plan: parseInt(mealPlanId) }),
    ];

    if (updateCreateEntries.length > 0) {
      const updateBody = { meal_updates: updateCreateEntries };

      console.log("🔄 UPDATE/CREATE request:", JSON.stringify(updateBody, null, 2));

      const updateResult = await callMealTimeUpdate(updateBody);
      console.log("🔄 UPDATE/CREATE response:", updateResult);

      if (updateResult.success !== "1") {
        throw new Error(updateResult.message || "Failed to update meal timings");
      }
    } else {
      console.log("✅ No changes to update/create — skipping second request");
    }

    // ── Success ────────────────────────────────────────────────────────────
    onSubmit(mealTimings);
    handleClose();
    onShowToast?.(
      "success",
      "Meal timings updated",
      "Your meal schedule has been successfully updated",
    );
  } catch (err: any) {
    console.error("Update error:", err);
    const message =
      err?.name === "AbortError"
        ? "Request timed out. Please check your connection and try again."
        : err instanceof Error
          ? err.message
          : "An error occurred while updating meal timings";

    onShowToast?.("error", "Update failed", message);
  } finally {
    setIsCreating(false);
    isSubmittingRef.current = false;
  }
};

  const handleCreate = async () => {
    if (!user?.nutrist_id) {
      console.error("No nutritionist ID found");
      onShowToast?.(
        "error",
        "Authentication required",
        "Unable to create meal plan: User not authenticated",
      );
      return;
    }

    if (!duplicateData?.sourceMealPlanId) {
      return;
    }

    setIsCreating(true);

    try {
      console.log(
        "🔍 Duplicating meal plan with ID:",
        duplicateData.sourceMealPlanId,
      );

      const cpfMinValue = `${step1Data.cpfRatio.min.c}:${step1Data.cpfRatio.min.p}:${step1Data.cpfRatio.min.f}`;
      const cpfMaxValue = `${step1Data.cpfRatio.max.c}:${step1Data.cpfRatio.max.p}:${step1Data.cpfRatio.max.f}`;

      const requestBody = {
        meal_plan_id: duplicateData.sourceMealPlanId,
        user_id: userId.toString(),
        nutritionist_id: user.nutrist_id.toString(),
        meal_plan_type: step1Data.planType,
        total_calories_min: parseFloat(step1Data.totalCalories.min),
        total_calories_max: parseFloat(step1Data.totalCalories.max),
        total_protein_min: parseFloat(step1Data.totalAdjProtein.min),
        total_protein_max: parseFloat(step1Data.totalAdjProtein.max),
        total_carbs_min: parseFloat(step1Data.totalNetCarbs.min),
        total_carbs_max: parseFloat(step1Data.totalNetCarbs.max),
        total_fats_min: parseFloat(step1Data.totalFats.min),
        total_fats_max: parseFloat(step1Data.totalFats.max),
        total_GL_min: parseFloat(step1Data.totalGL.min),
        total_GL_max: parseFloat(step1Data.totalGL.max),
        instructions: step1Data.instructions || "",
        CPF_min: cpfMinValue,
        CPF_max: cpfMaxValue,
      };

      console.log("🔍 Duplicating meal plan with data:", requestBody);

      const apiResponse = await getDuplicateMealPlanDetails(requestBody);
      console.log("✅ Meal plan duplicated successfully:", apiResponse);

      if (apiResponse.success === "1" && apiResponse.meal_plan) {
        const duplicatedPlan = apiResponse.meal_plan;

        const newMealPlan = {
          id: duplicatedPlan.meal_plan_id,
          plan_name: duplicatedPlan.meal_plan_name,
          meal_type: duplicatedPlan.meal_plan_type,
          created_by: user
            ? `${user.nutrist_fname || ""} ${user.nutrist_lname || ""}`.trim()
            : "Unknown",
          created_on: new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
          cal: parseFloat(duplicatedPlan.total_calories_max) || 0,
          adj_prot: parseFloat(duplicatedPlan.total_protein_max) || 0,
          carb: parseFloat(duplicatedPlan.total_carbs_max) || 0,
          fat: parseFloat(duplicatedPlan.total_fats_max) || 0,
          gl: parseFloat(duplicatedPlan.total_GL_max) || 0,
          status: "Draft" as const,
        };

        const finalData = {
          ...step1Data,
        };

        onSubmit(finalData);
        handleClose();

        navigate(
          `/client/${userId}/plans/meal/${duplicatedPlan.meal_plan_id}`,
          {
            state: {
              mealPlan: newMealPlan,
              isNewPlan: true,
              showSuccessToast: true,
              isDuplicated: true,
            },
          },
        );
      } else {
        throw new Error(
          apiResponse.message || "Failed to duplicate meal plan",
        );
      }
    } catch (err) {
      console.error("❌ Error duplicating meal plan:", err);
      onShowToast?.(
        "error",
        "Failed to duplicate meal plan",
        err instanceof Error ? err.message : "Please try again",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setStep1Data({
      planType: "",
      totalCalories: { min: "", max: "" },
      totalAdjProtein: { min: "", max: "" },
      totalNetCarbs: { min: "", max: "" },
      totalFats: { min: "", max: "" },
      totalGL: { min: "", max: "" },
      cpfRatio: {
        min: { c: "0", p: "0", f: "0" },
        max: { c: "0", p: "0", f: "0" },
      },
      instructions: "",
    });
    setMealTimings([]);
    setCurrentMeal({
      mealType: "",
      hour: "Hr",
      minute: "Min",
      period: "AM",
    });
    setSelectedMealId(null);
    setShowInstructions(false);
    setValidationErrors({});
    setTouchedFields({});
    setHasChanges(false);
    setInitialDuplicateData(null);
    setPlanTypeSearchTerm("");
    setShowPlanTypeDropdown(false);
    // ── NEW: Reset deleted meal IDs on close ──────────────────────────────
    setDeletedMealIds([]);
    deletedMealIdsRef.current = [];
    isSubmittingRef.current = false;
    // ── NEW: Reset instructions delete confirmation on close ──────────────
    setShowDeleteInstructionsConfirm(false);
    onClose();
  };

  const formatTime = (hour: string, minute: string, period: string) => {
    return `${hour}:${minute} ${period}`;
  };

  const shouldShowError = (field: string) => {
    return touchedFields[field] && validationErrors[field] !== undefined;
  };

  const isFieldEmpty = (value: string) => {
    return !value || value.trim() === "";
  };

  const shouldShowEmptyError = (field: string, value: string) => {
    return touchedFields[field] && isFieldEmpty(value);
  };

  const markFieldTouched = (field: string) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const getTimeInMinutes = (meal: MealTiming) => {
    let hour = parseInt(meal.time.hour, 10);
    const minute = parseInt(meal.time.minute, 10);
    const period = meal.time.period;

    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;

    return hour * 60 + minute;
  };

  const sortedMealTimings = [...mealTimings].sort(
    (a, b) => getTimeInMinutes(a) - getTimeInMinutes(b),
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="pt-5 py-3 mx-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F]">
              {isEditMode ? "Manage meal timings" : "Define meal parameters"}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!isEditMode ? (
            <div className="space-y-6">
              {/* Plan Type */}
              <div>
                <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                  Plan type
                </label>

                <div className="relative max-w-xs" ref={planTypeDropdownRef}>
                  <input
                    type="text"
                    value={planTypeSearchTerm}
                    onChange={(e) => {
                      setPlanTypeSearchTerm(e.target.value);
                      setShowPlanTypeDropdown(true);
                      markFieldTouched("planType");
                    }}
                    onFocus={() => setShowPlanTypeDropdown(true)}
                    placeholder={
                      isPlanTypesLoading
                        ? "Loading plan types..."
                        : "Search plan type"
                    }
                    disabled={isPlanTypesLoading}
                    className={`w-full px-4 py-2 border rounded-md text-sm 3xl:text-base font-inter outline-none focus:border-[#0DB5B5] ${
                      shouldShowEmptyError("planType", step1Data.planType)
                        ? "border-red-500"
                        : "border-[#E0E0E0]"
                    } ${isPlanTypesLoading ? "bg-gray-50 cursor-not-allowed" : ""}`}
                  />

                  {isPlanTypesLoading && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {showPlanTypeDropdown &&
                    filteredPlanTypes.length > 0 &&
                    !isPlanTypesLoading && (
                      <div
                        className="fixed bg-white border border-[#E5E5EA] rounded-md shadow-lg max-h-60 overflow-y-auto custom-scrollbar-overlay z-[60]"
                        style={{
                          top:
                            planTypeDropdownRef.current?.getBoundingClientRect()
                              .bottom! + 4,
                          left: planTypeDropdownRef.current?.getBoundingClientRect()
                            .left,
                          width:
                            planTypeDropdownRef.current?.getBoundingClientRect()
                              .width,
                        }}
                      >
                        {filteredPlanTypes.map((type) => (
                          <div
                            key={type}
                            onClick={() => {
                              setStep1Data({ ...step1Data, planType: type });
                              setPlanTypeSearchTerm(type);
                              setShowPlanTypeDropdown(false);
                              markFieldTouched("planType");
                            }}
                            className="m-2 px-2 py-1.5 hover:bg-[#E5F8F8] rounded-md cursor-pointer text-sm 3xl:text-base"
                          >
                            {type}
                          </div>
                        ))}
                      </div>
                    )}
                  {showPlanTypeDropdown &&
                    !isPlanTypesLoading &&
                    planTypeSearchTerm.length > 0 &&
                    filteredPlanTypes.length === 0 && (
                      <div
                        className="fixed bg-white border border-[#E5E5EA] rounded-md shadow-lg z-[60]"
                        style={{
                          top:
                            planTypeDropdownRef.current?.getBoundingClientRect()
                              .bottom! + 4,
                          left: planTypeDropdownRef.current?.getBoundingClientRect()
                            .left,
                          width:
                            planTypeDropdownRef.current?.getBoundingClientRect()
                              .width,
                        }}
                      >
                        <p className="px-4 py-3 text-sm 3xl:text-base text-[#8E8E93]">
                          No plan types found
                        </p>
                      </div>
                    )}
                </div>

                {shouldShowEmptyError("planType", step1Data.planType) && (
                  <p className="mt-1 text-xs 3xl:text-sm text-red-500">
                    This field is required
                  </p>
                )}
              </div>

              {/* Grid for parameters */}
              <div className="grid grid-cols-3 gap-6">
                {/* Total Calories */}
                <div>
                  <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                    Total calories{" "}
                    <span className="text-[#8E8E93] font-normal">
                      (kcals)
                    </span>{" "}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={step1Data.totalCalories.min}
                      onChange={(e) => {
                        if (!isCaloriesValueValid(e.target.value)) return;
                        setStep1Data({
                          ...step1Data,
                          totalCalories: {
                            ...step1Data.totalCalories,
                            min: e.target.value,
                          },
                        });
                        markFieldTouched("totalCalories");
                      }}
                      onBlur={() => markFieldTouched("totalCalories")}
                      className={`w-full px-4 py-2 border ${
                        shouldShowEmptyError(
                          "totalCalories",
                          step1Data.totalCalories.min,
                        ) || shouldShowError("totalCalories")
                          ? "border-red-500"
                          : "border-[#E0E0E0]"
                      } rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                    />
                    <span className="text-[#8E8E93]">to</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={step1Data.totalCalories.max}
                      onChange={(e) => {
                        if (!isCaloriesValueValid(e.target.value)) return;
                        setStep1Data({
                          ...step1Data,
                          totalCalories: {
                            ...step1Data.totalCalories,
                            max: e.target.value,
                          },
                        });
                        markFieldTouched("totalCalories");
                      }}
                      onBlur={() => markFieldTouched("totalCalories")}
                      className={`w-full px-4 py-2 border ${
                        shouldShowEmptyError(
                          "totalCalories",
                          step1Data.totalCalories.max,
                        ) || shouldShowError("totalCalories")
                          ? "border-red-500"
                          : "border-[#E0E0E0]"
                      } rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                    />
                  </div>
                  {shouldShowError("totalCalories") && (
                    <p className="mt-1 text-xs 3xl:text-sm text-red-500">
                      {validationErrors.totalCalories}
                    </p>
                  )}
                  {!shouldShowError("totalCalories") &&
                    touchedFields.totalCalories &&
                    (isFieldEmpty(step1Data.totalCalories.min) ||
                      isFieldEmpty(step1Data.totalCalories.max)) && (
                      <p className="mt-1 text-xs 3xl:text-sm text-red-500">
                        Both min and max values are required
                      </p>
                    )}
                  <p className="mt-1 text-xs 3xl:text-sm text-[#8E8E93]">
                    Max 4 digits (up to 9999)
                  </p>
                </div>

                {/* Total Adj.Protein */}
                <div>
                  <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                    Total Adj.Protein{" "}
                    <span className="text-[#8E8E93] font-normal">(g)</span>{" "}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={step1Data.totalAdjProtein.min}
                      onChange={(e) => {
                        setStep1Data({
                          ...step1Data,
                          totalAdjProtein: {
                            ...step1Data.totalAdjProtein,
                            min: e.target.value,
                          },
                        });
                        markFieldTouched("totalAdjProtein");
                      }}
                      onBlur={() => markFieldTouched("totalAdjProtein")}
                      className={`w-full px-4 py-2 border ${
                        shouldShowEmptyError(
                          "totalAdjProtein",
                          step1Data.totalAdjProtein.min,
                        ) || shouldShowError("totalAdjProtein")
                          ? "border-red-500"
                          : "border-[#E0E0E0]"
                      } rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                    />
                    <span className="text-[#8E8E93]">to</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={step1Data.totalAdjProtein.max}
                      onChange={(e) => {
                        setStep1Data({
                          ...step1Data,
                          totalAdjProtein: {
                            ...step1Data.totalAdjProtein,
                            max: e.target.value,
                          },
                        });
                        markFieldTouched("totalAdjProtein");
                      }}
                      onBlur={() => markFieldTouched("totalAdjProtein")}
                      className={`w-full px-4 py-2 border ${
                        shouldShowEmptyError(
                          "totalAdjProtein",
                          step1Data.totalAdjProtein.max,
                        ) || shouldShowError("totalAdjProtein")
                          ? "border-red-500"
                          : "border-[#E0E0E0]"
                      } rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                    />
                  </div>
                  {shouldShowError("totalAdjProtein") && (
                    <p className="mt-1 text-xs 3xl:text-sm text-red-500">
                      {validationErrors.totalAdjProtein}
                    </p>
                  )}
                  {!shouldShowError("totalAdjProtein") &&
                    touchedFields.totalAdjProtein &&
                    (isFieldEmpty(step1Data.totalAdjProtein.min) ||
                      isFieldEmpty(step1Data.totalAdjProtein.max)) && (
                      <p className="mt-1 text-xs 3xl:text-sm text-red-500">
                        Both min and max values are required
                      </p>
                    )}
                </div>

                {/* Total Net Carbs */}
                <div>
                  <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                    Total Net Carbs{" "}
                    <span className="text-[#8E8E93] font-normal">(g)</span>{" "}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={step1Data.totalNetCarbs.min}
                      onChange={(e) => {
                        setStep1Data({
                          ...step1Data,
                          totalNetCarbs: {
                            ...step1Data.totalNetCarbs,
                            min: e.target.value,
                          },
                        });
                        markFieldTouched("totalNetCarbs");
                      }}
                      onBlur={() => markFieldTouched("totalNetCarbs")}
                      className={`w-full px-4 py-2 border ${
                        shouldShowEmptyError(
                          "totalNetCarbs",
                          step1Data.totalNetCarbs.min,
                        ) || shouldShowError("totalNetCarbs")
                          ? "border-red-500"
                          : "border-[#E0E0E0]"
                      } rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                    />
                    <span className="text-[#8E8E93]">to</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={step1Data.totalNetCarbs.max}
                      onChange={(e) => {
                        setStep1Data({
                          ...step1Data,
                          totalNetCarbs: {
                            ...step1Data.totalNetCarbs,
                            max: e.target.value,
                          },
                        });
                        markFieldTouched("totalNetCarbs");
                      }}
                      onBlur={() => markFieldTouched("totalNetCarbs")}
                      className={`w-full px-4 py-2 border ${
                        shouldShowEmptyError(
                          "totalNetCarbs",
                          step1Data.totalNetCarbs.max,
                        ) || shouldShowError("totalNetCarbs")
                          ? "border-red-500"
                          : "border-[#E0E0E0]"
                      } rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                    />
                  </div>
                  {shouldShowError("totalNetCarbs") && (
                    <p className="mt-1 text-xs 3xl:text-sm text-red-500">
                      {validationErrors.totalNetCarbs}
                    </p>
                  )}
                  {!shouldShowError("totalNetCarbs") &&
                    touchedFields.totalNetCarbs &&
                    (isFieldEmpty(step1Data.totalNetCarbs.min) ||
                      isFieldEmpty(step1Data.totalNetCarbs.max)) && (
                      <p className="mt-1 text-xs 3xl:text-sm text-red-500">
                        Both min and max values are required
                      </p>
                    )}
                </div>

                {/* Total Fats */}
                <div>
                  <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                    Total fats{" "}
                    <span className="text-[#8E8E93] font-normal">(g)</span>{" "}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={step1Data.totalFats.min}
                      onChange={(e) => {
                        setStep1Data({
                          ...step1Data,
                          totalFats: {
                            ...step1Data.totalFats,
                            min: e.target.value,
                          },
                        });
                        markFieldTouched("totalFats");
                      }}
                      onBlur={() => markFieldTouched("totalFats")}
                      className={`w-full px-4 py-2 border ${
                        shouldShowEmptyError(
                          "totalFats",
                          step1Data.totalFats.min,
                        ) || shouldShowError("totalFats")
                          ? "border-red-500"
                          : "border-[#E0E0E0]"
                      } rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                    />
                    <span className="text-[#8E8E93]">to</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={step1Data.totalFats.max}
                      onChange={(e) => {
                        setStep1Data({
                          ...step1Data,
                          totalFats: {
                            ...step1Data.totalFats,
                            max: e.target.value,
                          },
                        });
                        markFieldTouched("totalFats");
                      }}
                      onBlur={() => markFieldTouched("totalFats")}
                      className={`w-full px-4 py-2 border ${
                        shouldShowEmptyError(
                          "totalFats",
                          step1Data.totalFats.max,
                        ) || shouldShowError("totalFats")
                          ? "border-red-500"
                          : "border-[#E0E0E0]"
                      } rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                    />
                  </div>
                  {shouldShowError("totalFats") && (
                    <p className="mt-1 text-xs 3xl:text-sm text-red-500">
                      {validationErrors.totalFats}
                    </p>
                  )}
                  {!shouldShowError("totalFats") &&
                    touchedFields.totalFats &&
                    (isFieldEmpty(step1Data.totalFats.min) ||
                      isFieldEmpty(step1Data.totalFats.max)) && (
                      <p className="mt-1 text-xs 3xl:text-sm text-red-500">
                        Both min and max values are required
                      </p>
                    )}
                </div>

                {/* Total GL */}
                <div>
                  <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                    Total GL
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={step1Data.totalGL.min}
                      onChange={(e) => {
                        setStep1Data({
                          ...step1Data,
                          totalGL: {
                            ...step1Data.totalGL,
                            min: e.target.value,
                          },
                        });
                        markFieldTouched("totalGL");
                      }}
                      onBlur={() => markFieldTouched("totalGL")}
                      className={`w-full px-4 py-2 border ${
                        shouldShowEmptyError(
                          "totalGL",
                          step1Data.totalGL.min,
                        ) || shouldShowError("totalGL")
                          ? "border-red-500"
                          : "border-[#E0E0E0]"
                      } rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                    />
                    <span className="text-[#8E8E93]">to</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={step1Data.totalGL.max}
                      onChange={(e) => {
                        setStep1Data({
                          ...step1Data,
                          totalGL: {
                            ...step1Data.totalGL,
                            max: e.target.value,
                          },
                        });
                        markFieldTouched("totalGL");
                      }}
                      onBlur={() => markFieldTouched("totalGL")}
                      className={`w-full px-4 py-2 border ${
                        shouldShowEmptyError(
                          "totalGL",
                          step1Data.totalGL.max,
                        ) || shouldShowError("totalGL")
                          ? "border-red-500"
                          : "border-[#E0E0E0]"
                      } rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5]`}
                    />
                  </div>
                  {shouldShowError("totalGL") && (
                    <p className="mt-1 text-xs 3xl:text-sm text-red-500">
                      {validationErrors.totalGL}
                    </p>
                  )}
                  {!shouldShowError("totalGL") &&
                    touchedFields.totalGL &&
                    (isFieldEmpty(step1Data.totalGL.min) ||
                      isFieldEmpty(step1Data.totalGL.max)) && (
                      <p className="mt-1 text-xs 3xl:text-sm text-red-500">
                        Both min and max values are required
                      </p>
                    )}
                </div>

                {/* C:P:F Ratio */}
                <div>
                  <label className=" text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2 flex items-center justify-between gap-1">
                    C:P:F ratio
                    <Info className="w-4 h-4 text-[#8E8E93]" />
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="w-full px-4 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter bg-gray-50 text-[#8E8E93]">
                      {step1Data.cpfRatio.min.c} : {step1Data.cpfRatio.min.p} :{" "}
                      {step1Data.cpfRatio.min.f}
                    </div>
                    <span className="text-[#8E8E93]">to</span>
                    <div className="w-full px-4 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter bg-gray-50 text-[#8E8E93]">
                      {step1Data.cpfRatio.max.c} : {step1Data.cpfRatio.max.p} :{" "}
                      {step1Data.cpfRatio.max.f}
                    </div>
                  </div>
                </div>
              </div>

              {/* Add Instructions */}
              {!showInstructions ? (
                <div>
                  <button
                    onClick={() => setShowInstructions(true)}
                    className="flex items-center gap-2 text-[#0DB5B5] text-sm 3xl:text-base font-medium hover:text-[#0CA0A0] transition-colors"
                  >
                    <span className="text-xl">+</span>
                    Add instructions
                  </button>
                  <p className="text-xs 3xl:text-sm text-[#8E8E93] mt-1">
                    Add guidance to help the client follow this meal plan
                    effectively.
                  </p>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F]">
                      Add{" "}
                      <span className="text-[#8E8E93] font-normal">
                        (Optional)
                      </span>
                    </label>

                    {/* ── Delete confirmation inline — matches SupplementCard pattern ── */}
                    {showDeleteInstructionsConfirm ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm 3xl:text-base text-[#1F1F1F]">
                          Delete instructions?
                        </span>
                        <Button
                          variant="danger"
                          size="xs"
                          onClick={() => {
                            setShowInstructions(false);
                            setStep1Data({ ...step1Data, instructions: "" });
                            setShowDeleteInstructionsConfirm(false);
                          }}
                        >
                          Confirm delete
                        </Button>
                        <button
                          onClick={() => setShowDeleteInstructionsConfirm(false)}
                          className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors p-0.5 rounded border-[0.5px] border-[#1F1F1F]"
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path
                              d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowDeleteInstructionsConfirm(true)}
                        className="text-[#FF3B30] hover:text-[#E63228] transition-colors"
                      >
                        <DeleteIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <textarea
                    placeholder="e.g., Avoid fried foods, prefer steaming, complete meal within 30 minutes"
                    value={step1Data.instructions}
                    onChange={(e) =>
                      setStep1Data({
                        ...step1Data,
                        instructions: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter focus:outline-none focus:border-[#0DB5B5] resize-none"
                    rows={4}
                  />
                </div>
              )}
            </div>
          ) : (
            // Step 2: Meal timing & schedule
            <div className="grid grid-cols-6 gap-6">
              {/* Left side - Meal list */}
              <div className="col-span-4  bg-[#FBFBFB] rounded-md p-4 ">
                <div>
                  {mealTimings.length === 0 ? (
                    <div className="flex items-center justify-center h-52 ">
                      <div className="text-center">
                        <p className="text-[#828282] text-sm 3xl:text-base 3xl:text-base">
                          No meals scheduled yet
                        </p>
                        <p className="text-[#8E8E93] text-sm 3xl:text-base 3xl:text-base mt-1">
                          Add meal timing using the form on the right.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className=" grid grid-cols-2 gap-4">
                      {sortedMealTimings.map((meal) => (
                        <MealCard
                          key={meal.id}
                          meal={meal}
                          isSelected={selectedMealId === meal.id}
                          showMenu={mealTimings.length > 1}
                          onSelect={() => handleSelectMeal(meal)}
                          onDelete={() => handleDeleteMeal(meal.id)}
                          formatTime={formatTime}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right side - Add/Edit form */}
              <div className="col-span-2 border-[0.5px] border-[#E0E0E0] rounded-md p-4">
                <div className="space-y-4">
                  {/* Meal Type */}
                  <div>
                    <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                      Meal type
                    </label>
                    <div className="relative">
                      {selectedMealId ? (
                        <div className="w-full px-4 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter bg-gray-50 text-[#202020]">
                          {currentMeal.mealType}
                        </div>
                      ) : (
                        <CustomDropdown
                          value={currentMeal.mealType}
                          placeholder="Select"
                          options={availableMealTypes}
                          onChange={(val) =>
                            setCurrentMeal({ ...currentMeal, mealType: val })
                          }
                        />
                      )}
                    </div>
                  </div>

                  {/* Time */}
                  <div>
                    <label className="block text-sm 3xl:text-base font-medium text-[#1F1F1F] mb-2">
                      Time
                    </label>
                    <div className="flex items-center gap-2">
                      <CustomDropdown
                        value={
                          currentMeal.hour !== "Hr" ? currentMeal.hour : ""
                        }
                        placeholder="Hr"
                        options={hours}
                        onChange={(val) =>
                          setCurrentMeal({ ...currentMeal, hour: val })
                        }
                      />

                      <CustomDropdown
                        value={
                          currentMeal.minute !== "Min" ? currentMeal.minute : ""
                        }
                        placeholder="Min"
                        options={minutes}
                        onChange={(val) =>
                          setCurrentMeal({ ...currentMeal, minute: val })
                        }
                      />

                      <CustomDropdown
                        value={currentMeal.period}
                        placeholder="AM"
                        options={["AM", "PM"]}
                        onChange={(val) =>
                          setCurrentMeal({ ...currentMeal, period: val })
                        }
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleReset}
                      className="px-4 py-2 text-sm 3xl:text-base font-medium text-[#646464] hover:text-[#1F1F1F] transition-colors"
                    >
                      Reset
                    </button>
                    <Button
                      variant="primary"
                      size="md"
                      onClick={handleAddOrUpdateMeal}
                      disabled={
                        !currentMeal.mealType ||
                        currentMeal.hour === "Hr" ||
                        currentMeal.minute === "Min"
                      }
                    >
                      {selectedMealId ? "Update" : "Add"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className=" py-4 mx-6 border-t border-gray-200 flex items-center justify-end gap-4 flex-shrink-0">
          {!isEditMode ? (
            <>
              <Button
                variant="secondary"
                size="md"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleCreate}
                disabled={!isStep1Complete()}
              >
                {isCreating && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                {isCreating ? "Creating..." : "Create"}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                size="md"
                onClick={handleClose}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleUpdate}
                disabled={isCreating || mealTimings.length === 0}
              >
                {isCreating && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                {isCreating ? "Updating..." : "Update"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MealPlanDuplicateAndTimingsPopup;